// packages/ui/src/components/chat/outlinedFormFields.tsx
'use client';

// The pre-chat form's fields: FormTextField and PhoneInput (with its country
// selector), the components every other form uses, drawn here with
// OutlinedTextField instead of MUI's TextField. With them chat.js carries no MUI
// TextField, so none of its Select, Menu, Popover and Modal either
// (docs/embed-size-and-speed.md §4, textfield-outlined-only). Only the chat
// widget's components use these: the shop draws selects, so it keeps TextField.
import type { FieldValues } from 'react-hook-form';
import { useFormTextField, type FormTextFieldProps, type TextFieldComponent } from '@tourtask/ui/forms/fields/FormTextField';
import { useFormAutocomplete, type FormAutocompleteProps } from '@tourtask/ui/forms/fields/FormAutocomplete';
import { useCountrySelectorInput, type CountrySelectorInputProps } from '@tourtask/ui/forms/fields/CountrySelectorInput';
import { usePhoneInput, type PhoneInputProps } from '@tourtask/ui/forms/fields/PhoneInput';
import { useStableAutocomplete, type StableAutocompleteProps } from '@tourtask/ui/inputs/StableAutocomplete';
import { OutlinedTextField } from '@tourtask/ui/inputs/OutlinedTextField';

// TextField's props, which these fields pass on: OutlinedTextField draws its outlined, non-select path, and the
// pre-chat form's fields ask for neither a select nor another variant (it warns in development if one is).
const OutlinedField = OutlinedTextField as TextFieldComponent;

export function OutlinedFormTextField<TFieldValues extends FieldValues>(props: FormTextFieldProps<TFieldValues>) {
  return useFormTextField(OutlinedField, props);
}

function OutlinedStableAutocomplete<T>(props: StableAutocompleteProps<T>) {
  return useStableAutocomplete(OutlinedField, props);
}

function OutlinedFormAutocomplete<TFieldValues extends FieldValues, TOption>(props: FormAutocompleteProps<TFieldValues, TOption>) {
  return useFormAutocomplete(OutlinedStableAutocomplete, props);
}

function OutlinedCountrySelectorInput<TFieldValues extends FieldValues>(props: CountrySelectorInputProps<TFieldValues>) {
  return useCountrySelectorInput(OutlinedFormAutocomplete, props);
}

export function OutlinedPhoneInput<TFieldValues extends FieldValues>(props: PhoneInputProps<TFieldValues>) {
  return usePhoneInput(OutlinedFormTextField, OutlinedCountrySelectorInput, props);
}
